import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { FiPlus, FiTrash2, FiUsers } from "react-icons/fi";
import Overlay from "../../../Componentes/Overlay";
import Modal from "../../../Componentes/Utils/Modal";
import { useModal } from "../../../contexts/ModalContext";
import { useToast } from "../../../contexts/ToastContext";
import useApi from "../../../hooks/useApi";
import ModalAdicionarResponsavel from "./ModalAdicionarResponsavel";
import ModalConfirmRemoverResponsavel from "./ModalConfirmRemoverResponsavel";

const Container = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
`;

const Titulo = styled.h2`
    margin: 0;
    font-size: 18px;
    color: #263b4b;
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
        color: #2563eb;
    }
`;

const ContaInfo = styled.p`
    margin: 0;
    font-size: 13px;
    color: #64748b;
`;

const BotaoNovo = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background-color: #2563eb;
    color: #fff;
    padding: 8px 14px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
        background-color: #1d4ed8;
    }
`;

const Tabela = styled.table`
    width: 100%;
    border-collapse: collapse;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    overflow: hidden;
    background: #fff;

    th {
        text-align: left;
        padding: 10px 14px;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: #64748b;
        background: #f8fafc;
        border-bottom: 1px solid #e2e8f0;
    }

    td {
        padding: 10px 14px;
        font-size: 13px;
        color: #1e293b;
        border-top: 1px solid #f1f5f9;
    }

    td.acao {
        width: 48px;
        text-align: center;
    }
`;

const BotaoLixeira = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    cursor: pointer;
    color: #b91c1c;

    &:hover {
        background-color: rgba(185, 28, 28, 0.12);
    }
`;

const Vazio = styled.div`
    padding: 28px 16px;
    text-align: center;
    color: #64748b;
    font-size: 13px;
    border: 1px dashed #cbd5e1;
    border-radius: 10px;
    background-color: #f8fafc;
`;

const Carregando = styled(Vazio)`
    color: #475569;
`;

const ModalResponsaveis = ({ conta, descricao, onSaved }) => {
    const api = useApi();
    const { closeModal, openModal, isModalOpen } = useModal();
    const { notifyError } = useToast();

    const [loading, setLoading] = useState(true);
    const [responsaveis, setResponsaveis] = useState([]);
    const [usuarioParaRemover, setUsuarioParaRemover] = useState(null);

    const fetchResponsaveis = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(
                "/Painel/API/Contabilidade/Responsaveis/List/index.php",
                { params: { conta } }
            );
            setResponsaveis(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error(error);
            notifyError("Erro ao carregar responsáveis.");
            setResponsaveis([]);
        } finally {
            setLoading(false);
        }
    }, [api, conta, notifyError]);

    useEffect(() => {
        fetchResponsaveis();
    }, [fetchResponsaveis]);

    const handleAfterChange = useCallback(async () => {
        await fetchResponsaveis();
        onSaved?.();
    }, [fetchResponsaveis, onSaved]);

    const handleAbrirAdicionar = () => {
        openModal("contabilidadeAddResponsavel");
    };

    const handleAbrirRemover = (item) => {
        setUsuarioParaRemover(item);
        openModal("contabilidadeRemoveResponsavel");
    };

    const handleFecharRemover = () => {
        setUsuarioParaRemover(null);
        closeModal();
    };

    const userIdsAtuais = responsaveis.map((r) => r.user_id);

    return (
        <>
            <Overlay state changeState={closeModal}>
                <Modal header close={closeModal} size="lg">
                    <Container>
                        <Header>
                            <div>
                                <Titulo>
                                    <FiUsers size={20} />
                                    Responsáveis pela conta
                                </Titulo>
                                <ContaInfo>
                                    {conta} — {descricao || "-"}
                                </ContaInfo>
                            </div>
                            <BotaoNovo type="button" onClick={handleAbrirAdicionar}>
                                <FiPlus size={16} /> ADICIONAR
                            </BotaoNovo>
                        </Header>

                        {loading ? (
                            <Carregando>Carregando responsáveis...</Carregando>
                        ) : responsaveis.length === 0 ? (
                            <Vazio>
                                Nenhum responsável cadastrado para esta conta.
                                <br />
                                Clique em <strong>ADICIONAR</strong> para começar.
                            </Vazio>
                        ) : (
                            <Tabela>
                                <thead>
                                    <tr>
                                        <th>Usuário</th>
                                        <th>E-mail</th>
                                        <th className="acao" aria-label="Ações" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {responsaveis.map((item) => (
                                        <tr key={item.user_id}>
                                            <td>{item.usuario}</td>
                                            <td>{item.email || "-"}</td>
                                            <td className="acao">
                                                <BotaoLixeira
                                                    type="button"
                                                    title="Remover responsável"
                                                    aria-label="Remover responsável"
                                                    onClick={() => handleAbrirRemover(item)}
                                                >
                                                    <FiTrash2 size={16} />
                                                </BotaoLixeira>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Tabela>
                        )}
                    </Container>
                </Modal>
            </Overlay>

            {isModalOpen("contabilidadeAddResponsavel") && (
                <ModalAdicionarResponsavel
                    conta={conta}
                    userIdsAtuais={userIdsAtuais}
                    onSaved={handleAfterChange}
                />
            )}

            {isModalOpen("contabilidadeRemoveResponsavel") && (
                <ModalConfirmRemoverResponsavel
                    conta={conta}
                    item={usuarioParaRemover}
                    userIdsAtuais={userIdsAtuais}
                    fecharModal={handleFecharRemover}
                    onRemoved={handleAfterChange}
                />
            )}
        </>
    );
};

export default ModalResponsaveis;
